import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Copy, Check, TrendingUp, Send, Share2, Trophy, Users } from "lucide-react";

const FUNNELS = [
  { key: "join", label: "Join for $29/mo" },
  { key: "postcard", label: "Get on a postcard" },
  { key: "zip", label: "Share a ZIP / coverage" },
  { key: "site", label: "Just the website" },
];

const DownlineSection = () => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/my/downline").then((r) => setT(r.data)).catch(() => {}); }, []);
  if (!t) return null;
  return (
    <div className="mt-5 rounded-xl border border-border bg-card p-4" data-testid="downline-section">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-sm font-semibold text-sam-navy flex items-center gap-1.5"><Users className="h-4 w-4 text-sam-navy" />Your team (downline)</div>
        {t.override_unlocked
          ? <span className="inline-flex items-center gap-1 rounded-full bg-sam-green/15 text-sam-green text-xs font-semibold px-2.5 py-1" data-testid="downline-override-on"><Trophy className="h-3.5 w-3.5" />Overrides unlocked · {t.override_l1_pct}% L1 / {t.override_l2_pct}% L2</span>
          : <span className="text-xs text-sam-slate" data-testid="downline-override-locked">Reach {t.max_bonus_cards} cards to unlock team overrides ({t.my_active_cards}/{t.max_bonus_cards})</span>}
      </div>
      {t.members.length === 0 ? (
        <p className="text-xs text-sam-slate">No recruits yet — share your link above to start building your team. You'll earn {t.override_l1_pct}% on their sales once you hit max bonus.</p>
      ) : (
        <>
          <div className="flex gap-4 text-xs text-sam-slate mb-2">
            <span><b className="text-sam-navy">{t.l1_count}</b> recruited</span>
            <span><b className="text-sam-navy">{t.l1_active_members}</b> active</span>
            <span><b className="text-sam-navy">{t.l1_active_cards}</b> team cards</span>
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {t.members.map((m, i) => (
              <div key={i} className="flex items-center justify-between gap-2 border-b border-border/50 pb-1 text-xs" data-testid={`downline-member-${i}`}>
                <span className="text-sam-navy font-medium">{m.name} <span className="font-normal text-sam-slate">{m.email_masked}</span></span>
                <span className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 ${m.active ? "bg-sam-green/15 text-sam-green" : "bg-sam-navy/10 text-sam-slate"}`}>{m.rank}</span>
                  <span className="text-sam-slate">{m.active_cards} card{m.active_cards === 1 ? "" : "s"}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      {t.l2_count > 0 && (
        <div className="mt-3 pt-3 border-t border-border/60" data-testid="downline-l2">
          <div className="text-xs font-semibold text-sam-navy mb-1">Second level ({t.override_l2_pct}% override) — {t.l2_count} {t.l2_count === 1 ? "person" : "people"}, {t.l2_active_cards} cards</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {t.l2_members.map((m, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-[11px] text-sam-slate" data-testid={`downline-l2-member-${i}`}>
                <span>{m.name} <span className="opacity-70">via {m.via}</span></span>
                <span>{m.rank} · {m.active_cards}c</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const GrowPanel = () => {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState("");
  const [funnel, setFunnel] = useState("join");
  const [channel, setChannel] = useState("email");
  const [to, setTo] = useState({ to_name: "", to_email: "", to_phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/my/affiliate").then((r) => setD(r.data)).catch(() => {}); }, []);

  const copy = async (key, link) => {
    try { await navigator.clipboard.writeText(link); setCopied(key); setTimeout(() => setCopied(""), 1600); toast.success("Link copied"); }
    catch { toast.error("Could not copy — long-press to copy the link"); }
  };

  const send = async () => {
    if (!to.to_email && !to.to_phone) return toast.error("Add an email or phone to invite");
    setBusy(true);
    try {
      const { data } = await api.post("/my/invite", { ...to, funnel, channel });
      const parts = [data.sent?.email && "email", data.sent?.sms && "text"].filter(Boolean);
      toast.success(`Invite sent${parts.length ? " by " + parts.join(" & ") : ""}`);
      setTo({ to_name: "", to_email: "", to_phone: "", message: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the invite"); }
    finally { setBusy(false); }
  };

  if (!d) return null;
  const link = (d.links || {})[funnel] || "";
  const max = d.max_bonus_cards || 15;
  const pct = Math.min(100, Math.round(((d.active_cards || 0) / max) * 100));

  return (
    <div className="mb-6 rounded-2xl border border-sam-crimson/25 bg-gradient-to-br from-sam-crimson/5 to-transparent p-5" data-testid="grow-panel">
      <div className="flex items-center gap-2 mb-4">
        <Share2 className="h-5 w-5 text-sam-crimson" />
        <h2 className="font-display text-xl font-bold text-sam-navy">Share &amp; Earn</h2>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-sam-navy/10 text-sam-navy text-xs font-semibold px-2.5 py-1" data-testid="grow-rank">
          <Trophy className="h-3.5 w-3.5" />{d.rank}{d.commission_pct ? ` · ${d.commission_pct}%` : ""}
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Progress + link */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4" data-testid="grow-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-sam-navy flex items-center gap-1"><TrendingUp className="h-4 w-4 text-sam-green" />Cards to max bonus</span>
              <span className="text-sam-slate"><b className="text-sam-navy">{d.active_cards}</b> / {max}</span>
            </div>
            <div className="mt-2 h-2.5 rounded-full bg-sam-navy/10 overflow-hidden">
              <div className="h-full bg-sam-crimson rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-xs text-sam-slate">
              {d.cards_to_max > 0
                ? <>{d.cards_to_max} more active card{d.cards_to_max === 1 ? "" : "s"} to max bonus — that's ~1 a month. {d.next_rank ? <>Next rank: <b className="text-sam-navy">{d.next_rank}</b> at {d.cards_to_next} more.</> : null}</>
                : <>🎉 Max bonus reached — now build your downline for team overrides.</>}
              {" "}· {d.spots_sold} ad spot{d.spots_sold === 1 ? "" : "s"} sold
            </p>
          </div>

          {!d.affiliate_active && (
            <div className="rounded-xl border border-sam-amber/40 bg-sam-amber/5 px-4 py-3 text-sm text-sam-navy" data-testid="grow-interview-note">
              Your link already tracks sign-ups. To start earning commissions, finish your quick interview.{" "}
              <Link to="/careers" className="font-semibold text-sam-crimson underline" data-testid="grow-interview-link">Start it here</Link>.
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-4">
            <Label className="text-xs text-sam-slate">Your personal link</Label>
            <div className="mt-1.5 flex gap-2">
              <Input readOnly value={link} className="font-mono text-xs" data-testid="grow-link-input" />
              <Button variant="outline" size="icon" onClick={() => copy(funnel, link)} data-testid="grow-copy-btn">
                {copied === funnel ? <Check className="h-4 w-4 text-sam-green" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {FUNNELS.map((f) => (
                <button key={f.key} type="button" onClick={() => setFunnel(f.key)} data-testid={`grow-funnel-${f.key}`}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${funnel === f.key ? "border-sam-crimson bg-sam-crimson/10 text-sam-crimson" : "border-border text-sam-slate hover:border-sam-navy/40"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Invite form */}
        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="grow-invite-form">
          <div className="text-sm font-semibold text-sam-navy">Invite someone directly</div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Name</Label><Input value={to.to_name} onChange={(e) => setTo({ ...to, to_name: e.target.value })} data-testid="grow-to-name" placeholder="Their name" /></div>
            <div>
              <Label className="text-xs">Send by</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="grow-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">Text / SMS</SelectItem>
                  <SelectItem value="both">Email + Text</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {channel !== "sms" && (
            <div><Label className="text-xs">Email</Label><Input type="email" value={to.to_email} onChange={(e) => setTo({ ...to, to_email: e.target.value })} data-testid="grow-to-email" placeholder="them@example.com" /></div>
          )}
          {channel !== "email" && (
            <div><Label className="text-xs">Mobile</Label><Input value={to.to_phone} onChange={(e) => setTo({ ...to, to_phone: e.target.value })} data-testid="grow-to-phone" placeholder="(555) 555-5555" /></div>
          )}
          <div><Label className="text-xs">Personal note (optional)</Label><Input value={to.message} onChange={(e) => setTo({ ...to, message: e.target.value })} data-testid="grow-message" placeholder="Hey — thought of you…" /></div>
          <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={send} disabled={busy} data-testid="grow-send-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send invite
          </Button>
          <p className="text-center text-[11px] text-sam-slate">They'll land on your <b>{FUNNELS.find((f) => f.key === funnel)?.label}</b> funnel, tagged to you.</p>
        </div>
      </div>

      <DownlineSection />
    </div>
  );
};
